import React, { useState, useEffect } from "react";
import { useSignIn, useAuth, useClerk } from "@clerk/clerk-react";

import { LoginForm } from "@/components/login-form";
import { AuthPageShell } from "@/components/auth-page-shell";
import { isDemoClaimReturnPath } from "@/utils/demoEntryAnalysisSession";
import { resolveSafeRedirectPath } from "../../lib/sessionExpired";

function getPostSignInPath(): string {
  const redirectUrl = new URLSearchParams(window.location.search).get("redirect_url");
  return resolveSafeRedirectPath(redirectUrl);
}

function getPostSignInUrl(): string {
  return `${window.location.origin}${getPostSignInPath()}`;
}

function SignInLoadingShell() {
  return (
    <div
      className="relative z-[1] flex min-h-svh w-full items-center justify-center bg-muted dark:bg-content-canvas"
      aria-busy="true"
    />
  );
}

function SignIn() {
  const { isLoaded, isSignedIn } = useAuth();
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const clerk = useClerk();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOAuthLoading] = useState<"oauth_google" | null>(null);

  const [isResetMode, setIsResetMode] = useState(false);
  const [resetStage, setResetStage] = useState<"request" | "verify">("request");
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    if (isLoaded && signInLoaded) {
      const urlParams = new URLSearchParams(window.location.search);
      const addOAuth = urlParams.get("addOAuth");

      if (addOAuth && signIn) {
        const strategy = addOAuth as "oauth_google";
        handleOAuthSignIn(strategy);
      }
    }
  }, [isLoaded, signInLoaded, signIn]);

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      const urlParams = new URLSearchParams(window.location.search);
      const addOAuth = urlParams.get("addOAuth");

      if (addOAuth) {
        return;
      }

      window.location.assign(getPostSignInPath());
    }
  }, [isLoaded, isSignedIn]);

  const handleOAuthSignIn = async (strategy: "oauth_google") => {
    if (!signIn) {
      setError("Sign-in is not available. Please try again later.");
      return;
    }
    setError("");
    setOAuthLoading(strategy);

    const urlParams = new URLSearchParams(window.location.search);
    const addOAuth = urlParams.get("addOAuth");
    const returnUrl =
      sessionStorage.getItem("oauthAddReturnUrl") || window.location.origin + "/profile";

    try {
      await signIn.authenticateWithRedirect({
        strategy,
        redirectUrl: addOAuth ? returnUrl : getPostSignInUrl(),
        redirectUrlComplete: addOAuth ? returnUrl : getPostSignInUrl(),
      });
    } catch (err: any) {
      console.error(`${strategy} sign-in error:`, err);
      setError(err.message || "Failed to sign in with Google. Please try again.");
      setOAuthLoading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isResetMode) return;
    setError("");
    setSuccessMessage("");
    setLoading(true);

    if (!signIn) {
      setError("Sign-in is not available. Please try again later.");
      setLoading(false);
      return;
    }

    try {
      const result = await signIn.create({
        identifier,
        password,
      });

      if (result.status === "complete") {
        if (result.createdSessionId) {
          try {
            if (!isSignedIn) {
              await clerk.setActive({ session: result.createdSessionId });
            }
            window.location.assign(getPostSignInPath());
          } catch (sessionError: any) {
            if (
              sessionError.errors?.[0]?.code === "session_exists" ||
              sessionError.message?.includes("session") ||
              sessionError.message?.includes("already")
            ) {
              window.location.assign(getPostSignInPath());
            } else {
              throw sessionError;
            }
          }
        } else if (isSignedIn) {
          window.location.assign(getPostSignInPath());
        } else {
          setError("Session creation failed. Please try again.");
        }
      } else if (result.status === "needs_first_factor" || result.status === "needs_second_factor") {
        setError(
          "Multi-factor authentication is required. This feature is not yet implemented in the custom form."
        );
      } else if (result.status === "needs_new_password") {
        setError("A new password is required. Please reset your password.");
      } else if (result.status === "needs_identifier") {
        setError("Please provide a valid email address.");
      } else {
        setError(
          `Sign-in incomplete. Status: ${result.status}. Please check your email for verification or contact support.`
        );
      }
    } catch (err: any) {
      if (err.errors && err.errors.length > 0) {
        const clerkError = err.errors[0];
        const errorMessage =
          clerkError?.message || clerkError?.longMessage || "Invalid email or password. Please try again.";
        setError(errorMessage);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStartReset = () => {
    setError("");
    setSuccessMessage("");
    setIsResetMode(true);
    setResetStage("request");
    setResetEmail(identifier || "");
    setResetCode("");
    setResetNewPassword("");
    setResetConfirmPassword("");
  };

  const handleBackToSignIn = () => {
    setIsResetMode(false);
    setResetStage("request");
    setError("");
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!signIn) {
      setError("Password reset is not available. Please try again later.");
      return;
    }

    if (!resetEmail) {
      setError("Please enter your email to reset your password.");
      return;
    }

    setResetLoading(true);
    try {
      const result = await signIn.create({
        strategy: "reset_password_email_code",
        identifier: resetEmail,
      } as any);

      if (result) {
        setResetStage("verify");
      }
    } catch (err: any) {
      if (err.errors && err.errors.length > 0) {
        const msg = err.errors[0].message || err.errors[0].longMessage || "Failed to start password reset.";
        setError(msg);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Failed to start password reset. Please try again.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!signIn) {
      setError("Password reset is not available. Please try again later.");
      return;
    }

    if (!resetCode || !resetNewPassword || !resetConfirmPassword) {
      setError("Please enter the verification code and both password fields.");
      return;
    }

    if (resetNewPassword !== resetConfirmPassword) {
      setError("New password and confirmation do not match.");
      return;
    }

    setResetLoading(true);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code: resetCode,
        password: resetNewPassword,
      } as any);

      if (result.status === "complete") {
        setSuccessMessage("Password reset successfully. Please sign in with your new password.");
        try {
          await clerk.signOut();
        } catch (signOutError) {
          console.error("Error signing out after password reset:", signOutError);
        }
        setIsResetMode(false);
        setResetStage("request");
        setResetCode("");
        setResetNewPassword("");
        setResetConfirmPassword("");
        setPassword("");
        setIdentifier(resetEmail);
      } else {
        setError("Password reset is not complete yet. Please try again.");
      }
    } catch (err: any) {
      if (err.errors && err.errors.length > 0) {
        const msg = err.errors[0].message || err.errors[0].longMessage || "Failed to reset password.";
        setError(msg);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError("Failed to reset password. Please try again.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetSubmit = (e: React.FormEvent) => {
    if (resetStage === "request") {
      void handleResetRequest(e);
    } else {
      void handleResetVerify(e);
    }
  };

  if (!isLoaded || !signInLoaded) {
    return <SignInLoadingShell />;
  }

  const demoEntryAnalysisGate = isDemoClaimReturnPath(
    new URLSearchParams(window.location.search).get("redirect_url")
  );

  return (
    <AuthPageShell>
        <LoginForm
          isResetMode={isResetMode}
          resetStage={resetStage}
          identifier={identifier}
          password={password}
          resetEmail={resetEmail}
          resetCode={resetCode}
          resetNewPassword={resetNewPassword}
          resetConfirmPassword={resetConfirmPassword}
          showPassword={false}
          showResetNewPassword={false}
          showResetConfirmPassword={false}
          loading={loading}
          resetLoading={resetLoading}
          oauthLoading={oauthLoading}
          error={error}
          successMessage={successMessage}
          demoEntryAnalysisGate={demoEntryAnalysisGate}
          onIdentifierChange={setIdentifier}
          onPasswordChange={setPassword}
          onResetEmailChange={setResetEmail}
          onResetCodeChange={setResetCode}
          onResetNewPasswordChange={setResetNewPassword}
          onResetConfirmPasswordChange={setResetConfirmPassword}
          onShowPasswordToggle={() => {}}
          onShowResetNewPasswordToggle={() => {}}
          onShowResetConfirmPasswordToggle={() => {}}
          onSubmit={handleSubmit}
          onResetSubmit={handleResetSubmit}
          onStartReset={handleStartReset}
          onBackToSignIn={handleBackToSignIn}
          onOAuthSignIn={handleOAuthSignIn}
        />
    </AuthPageShell>
  );
}

export default SignIn;
